import React, { useEffect, useState, useMemo } from 'react';
import { addRoleProfile } from '../services/api';
import { MdOutlineKeyboardArrowUp, MdKeyboardArrowDown } from 'react-icons/md';

const COST_ROLE_TYPES = [
  'Management',
  'Technical Architect',
  'Backend Developer',
  'Full-Stack Developer',
  'Mobile Developer',
  'AI / ML Engineer',
  'Cloud Engineer',
  'DevOps Engineer',
  'Automation / Performance / Security Tester',
  'Infrastructure Engineer',
  'Cybersecurity Engineer',
  'ITSM Consultant',
  'ERP / Enterprise Applications – Technical',
  'ERP / Enterprise Applications – Functional',
  'Business Analyst',
  'UX / UI Designer',
  'Integration Engineer',
  'Database Engineer / DBA',
  'Specialized Engineering',
  'Documentation & Governance',
];

export default function RoleProfilesPanel({ selectedRoles = [] }) {
  const [allProfiles, setAllProfiles] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [savingId, setSavingId] = useState(null); // for loading state on Save

  useEffect(() => {
    const url = (process.env.REACT_APP_API_URL || '') + '/master/role-profiles';
    fetch(url)
      .then((r) => r.json())
      .then((data) => {
        const list = (data.roleProfiles || []).map((p) => ({
          ...p,
          costRoleType: p.costRoleType || extractCostRoleType(p.notes) || 'Default JD',
          seniorityGuidance: p.seniorityGuidance || p.seniority || '',
          interfaces: p.interfaces || '',
          kpis: p.kpis || [],
          isCustomJD: Boolean(
            p.isCustomJD || p.costRoleType === 'Custom' || p.costRoleType === 'Custom JD'
          ),
          isEditing: false,
        }));
        setAllProfiles(list);
      })
      .catch((err) => console.error('Failed to load role profiles', err));
  }, []);

  function extractCostRoleType(notes = '') {
    const match = notes.match(/Cost role type:\s*([^·.]+)/i);
    return match ? match[1].trim() : null;
  }

  function getJdLabel(profile = {}) {
    const value = (profile.costRoleType || '').trim();
    const isCustom = Boolean(
      profile.isCustomJD || value === 'Custom' || value === 'Custom JD'
    );
    return isCustom ? 'Custom JD' : 'Default JD';
  }

  // Only show profiles that match roles currently in section 2
  // Preserve the chronology/order of selectedRoles when rendering profiles
  const profiles = useMemo(() => {
    if (!selectedRoles.length) return [];

    const normalize = (s = '') => (s || '').toLowerCase().trim();

    // helper to decide if profile matches a role key
    const matches = (profile, key) => {
      const name = normalize(profile.name);
      const costType = normalize(profile.costRoleType);
      return (
        name.includes(key) ||
        key.includes(name) ||
        costType.includes(key) ||
        key.includes(costType)
      );
    };

    // Build an ordered list by iterating selectedRoles in order
    const added = new Set();
    const ordered = [];

    for (const r of selectedRoles) {
      const keys = [];
      if (r.roleType) keys.push(normalize(r.roleType));
      if (r.name) keys.push(normalize(r.name));

      for (const key of keys) {
        for (const p of allProfiles) {
          if (added.has(p.id)) continue;
          if (matches(p, key)) {
            ordered.push(p);
            added.add(p.id);
          }
        }
      }
    }

    // Append any remaining profiles that still match any selected role (but weren't added)
    for (const p of allProfiles) {
      if (added.has(p.id)) continue;
      for (const r of selectedRoles) {
        const key1 = normalize(r.roleType);
        const key2 = normalize(r.name);
        if (matches(p, key1) || matches(p, key2)) {
          ordered.push(p);
          added.add(p.id);
          break;
        }
      }
    }

    return ordered;
  }, [allProfiles, selectedRoles]);

  const toggle = (id) => setExpandedId((prev) => (prev === id ? null : id));

  const updateField = (id, field, value) => {
    setAllProfiles((prev) =>
      prev.map((p) => {
        if (p.id !== id) return p;
        if (['responsibilities', 'skills', 'kpis'].includes(field)) {
          const arr = value
            .split('\n')
            .map((l) => l.trim())
            .filter(Boolean);
          return { ...p, [field]: arr };
        }
        return { ...p, [field]: value };
      })
    );
  };

  const startEdit = (id) => {
    setAllProfiles((prev) =>
      prev.map((p) => (p.id === id ? { ...p, isEditing: true } : p))
    );
    setExpandedId(id);
  };

  // ★ SAVE – write changes back into the roleProfiles array and exit edit mode
  const saveProfile = async (id) => {
    const profile = allProfiles.find((p) => p.id === id);
    if (!profile) return;

    setSavingId(id);

    try {
      // 1. Update local state (exit edit mode)
      setAllProfiles((prev) =>
        prev.map((p) =>
          p.id === id
            ? {
                ...p,
                isEditing: false,
                isCustomJD: Boolean(p.isCustomJD || p.costRoleType === 'Custom JD'),
                // ensure clean data
                name: p.name?.trim() || p.name,
                summary: p.summary?.trim() || '',
              }
            : p
        )
      );

      // 2. Persist to backend (per-user role profile)
      try {
        // backend expects a single profile object and requires auth
        console.debug('Persisting role profile to backend', profile);
        await addRoleProfile(profile);
      } catch (err) {
        console.warn('Failed to persist role profile to backend', err);
      }
    } catch (err) {
      console.error('Failed to save profile', err);
      // optionally show a toast / error message
    } finally {
      setSavingId(null);
    }
  };

  // Cancel edit without saving (re-fetch or just close edit flag)
  const cancelEdit = (id) => {
    setAllProfiles((prev) =>
      prev.map((p) => (p.id === id ? { ...p, isEditing: false } : p))
    );
  };

  const toText = (arr = []) => (arr || []).join('\n');

  return (
    <div className="role-profiles-panel">
      <div className="panel-header">
        <h3>3 · ROLE PROFILES</h3>
        <p className="panel-desc">
          Profiles shown below match the roles you added in{' '}
          <strong>2 · Roles, seniority & cost</strong>. Click <strong>Change</strong> to edit,
          then <strong>Save</strong> to keep the changes.
        </p>
      </div>

      {profiles.length === 0 ? (
        <div className="empty-state">
          No roles selected yet. Add roles in section 2 to see their profiles here.
        </div>
      ) : (
        <div className="profile-list">
          {profiles.map((p) => {
            const isOpen = expandedId === p.id;
            const isEditing = p.isEditing;
            const isSaving = savingId === p.id;

            return (
              <div key={p.id} className={`profile-item ${isOpen ? 'open' : ''}`}>
                {/* Header */}
                <button
                  type="button"
                  className="profile-header"
                  onClick={() => toggle(p.id)}
                  aria-expanded={isOpen}
                >
                  <div className="header-left">
                    <span className="profile-name">{p.name}</span>
                    {/* <span className="cost-badge">{getJdLabel(p)}</span> */}
                    {isEditing && <span className="editing-badge">EDITING</span>}
                  </div>
                  <span className={` ${isOpen ? 'open' : ''}`}>
                    {isOpen ? <MdOutlineKeyboardArrowUp /> : <MdKeyboardArrowDown />}
                  </span>
                </button>

                {/* Body */}
                {isOpen && (
                  <div className="profile-body">
                    {isEditing ? (
                      /* ========== EDIT MODE ========== */
                      <div className="edit-form">
                        <div className="form-group">
                          <label>Role title</label>
                          <input
                            type="text"
                            value={p.name || ''}
                            onChange={(e) => updateField(p.id, 'name', e.target.value)}
                          />
                        </div>

                        {/* <div className="form-group">
                          <label>JD type</label>
                          <select
                            value={getJdLabel(p)}
                            onChange={(e) => {
                              const next = e.target.value;
                              const nextIsCustom = next === 'Custom JD';
                              updateField(p.id, 'isCustomJD', nextIsCustom);
                              updateField(
                                p.id,
                                'costRoleType',
                                nextIsCustom ? '' : ''
                              );
                            }}
                          >
                          </select>
                        </div> */}

                        <div className="form-group">
                          <label>Cost role type</label>
                          <select
                            value={p.costRoleType}
                            onChange={(e) => {
                              const next = e.target.value;
                            }}
                          >
                            {COST_ROLE_TYPES.map((t) => (
                              <option key={t} value={t}>
                                {t}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="form-group">
                          <label>Mission</label>
                          <textarea
                            rows={3}
                            value={p.summary || ''}
                            onChange={(e) => updateField(p.id, 'summary', e.target.value)}
                          />
                        </div>

                        <div className="form-group">
                          <label>Key responsibilities (one per line)</label>
                          <textarea
                            rows={6}
                            value={toText(p.responsibilities)}
                            onChange={(e) =>
                              updateField(p.id, 'responsibilities', e.target.value)
                            }
                          />
                        </div>

                        <div className="form-group">
                          <label>Skills & experience (one per line)</label>
                          <textarea
                            rows={5}
                            value={toText(p.skills)}
                            onChange={(e) => updateField(p.id, 'skills', e.target.value)}
                          />
                        </div>

                        <div className="form-group">
                          <label>Seniority guidance</label>
                          <textarea
                            rows={2}
                            value={p.seniorityGuidance || p.seniority || ''}
                            onChange={(e) =>
                              updateField(p.id, 'seniorityGuidance', e.target.value)
                            }
                          />
                        </div>

                        <div className="form-group">
                          <label>Interfaces</label>
                          <textarea
                            rows={2}
                            value={p.interfaces || ''}
                            onChange={(e) =>
                              updateField(p.id, 'interfaces', e.target.value)
                            }
                          />
                        </div>

                        <div className="form-group">
                          <label>Success measures / KPIs (one per line)</label>
                          <textarea
                            rows={4}
                            value={toText(p.kpis)}
                            onChange={(e) => updateField(p.id, 'kpis', e.target.value)}
                          />
                        </div>

                        {/* ★ SAVE + Cancel buttons */}
                        <div className="profile-actions edit-actions">
                          <button
                            type="button"
                            className="btn-save"
                            onClick={() => saveProfile(p.id)}
                            disabled={isSaving}
                          >
                            {isSaving ? 'Saving…' : 'Save'}
                          </button>
                          <button
                            type="button"
                            className="btn-cancel"
                            onClick={() => cancelEdit(p.id)}
                            disabled={isSaving}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* ========== VIEW MODE ========== */
                      <div className="view-mode">
                        {p.summary && <p className="mission">{p.summary}</p>}

                        {p.responsibilities?.length > 0 && (
                          <section className="section">
                            <h4>KEY RESPONSIBILITIES</h4>
                            <ul>
                              {p.responsibilities.map((item, i) => (
                                <li key={i}>{item}</li>
                              ))}
                            </ul>
                          </section>
                        )}

                        {p.skills?.length > 0 && (
                          <section className="section">
                            <h4>SKILLS & EXPERIENCE</h4>
                            <ul>
                              {p.skills.map((item, i) => (
                                <li key={i}>{item}</li>
                              ))}
                            </ul>
                          </section>
                        )}

                        {(p.seniorityGuidance || p.seniority) && (
                          <section className="section">
                            <h4>SENIORITY GUIDANCE</h4>
                            <p>{p.seniorityGuidance || p.seniority}</p>
                          </section>
                        )}

                        {p.interfaces && (
                          <section className="section">
                            <h4>INTERFACES</h4>
                            <p>{p.interfaces}</p>
                          </section>
                        )}

                        {p.kpis?.length > 0 && (
                          <section className="section">
                            <h4>SUCCESS MEASURES (KPIs)</h4>
                            <ul>
                              {p.kpis.map((item, i) => (
                                <li key={i}>{item}</li>
                              ))}
                            </ul>
                          </section>
                        )}

                        {p.notes && <div className="why-note">{p.notes}</div>}

                        <div className="profile-actions">
                          <button
                            type="button"
                            className="btn-change"
                            onClick={() => startEdit(p.id)}
                          >
                            ✎ Change
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}